import OAuthClientCredentialsGrant from "../authentication/oauth-client-credentials-grant";
import OAuthPasswordGrant from "../authentication/oauth-password-grant";
import OAuthRefreshTokenGrant from "../authentication/oauth-refreshtoken-grant";
import { Nullable } from "../base-types";
import {
    EntityFactory,
    IEntityFactory,
    IOptionListFactory,
    IRelationFactory,
    OptionListFactory,
    RelationFactory,
} from "../factories";
import Guard from "../guard";
import { ILinkHelper, LinkHelper } from "../link-helper";
import { ILogger } from "../logging/logger";
import NullLogger from "../logging/null-logger";
import { EntityDefinitionMapper, EntityMapper, TypedEntityCreator } from "../mappers";
import { AggregateClient, IAggregateClient } from "./aggregates-client";
import { ApiClient, IApiClient } from "./api-client";
import { AssetsClient, IAssetsClient } from "./assets-client";
import { CommandsClient, ICommandsClient } from "./commands-client";
import { CultureClient, ICultureClient } from "./culture-client";
import { CultureLoader } from "./culture-loader";
import { EntitiesClient, IEntitiesClient } from "./entities-client";
import { EntityDefinitionsClient, IEntityDefinitionsClient } from "./entity-definitions-client";
import { IExtendedContentHubClient } from "./extended-client";
import { IInternalClient, InternalClient } from "./internal-client";
import { INotificationsClient, NotificationsClient } from "./notifications-client";
import { IOptionListClient, OptionListClient } from "./option-list-client";
import { IPagesClient, PagesClient } from "./pages-client";
import { IPermissionsClient, PermissionsClient } from "./permissions-client";
import { IPoliciesClient, PoliciesClient } from "./policies-client";
import { IQueryingClient, QueryingClient } from "./querying-client";
import { IRawClient, RawClient } from "./raw-client";
import { ISearchClient, SearchClient } from "./search-client";
import { ISettingsClient, SettingsClient } from "./settings-client";
import { IUploadClient, UploadClient } from "./upload-client";
import { IUsersClient, UsersClient } from "./users-client";
import { IVersionChecker, VersionChecker } from "./version-checker";

/**
 * Client for the Content Hub.
 */
export interface IContentHubClient {
    logger: ILogger;

    //#region Clients
    readonly api: IApiClient;
    readonly aggregates: IAggregateClient;
    readonly assets: IAssetsClient;
    readonly commands: ICommandsClient;
    readonly cultures: ICultureClient;
    readonly entities: IEntitiesClient;
    readonly entityDefinitions: IEntityDefinitionsClient;
    readonly notifications: INotificationsClient;
    readonly optionLists: IOptionListClient;
    readonly policies: IPoliciesClient;
    readonly querying: IQueryingClient;
    readonly raw: IRawClient;
    readonly settings: ISettingsClient;
    readonly users: IUsersClient;
    readonly pages: IPagesClient;
    readonly permissions: IPermissionsClient;
    readonly uploads: IUploadClient;
    readonly versionChecker: IVersionChecker;
    //#endregion

    //#region Factories
    readonly entityFactory: IEntityFactory;
    readonly optionListFactory: IOptionListFactory;
    //#endregion
}

export class ContentHubClient implements IExtendedContentHubClient {
    logger: ILogger;

    baseUri: URI;
    readonly internalClient: IInternalClient;

    //#region Clients
    readonly api: IApiClient;
    readonly aggregates: IAggregateClient;
    readonly assets: IAssetsClient;
    readonly commands: ICommandsClient;
    readonly cultures: ICultureClient;
    readonly entities: IEntitiesClient;
    readonly entityDefinitions: IEntityDefinitionsClient;
    readonly notifications: INotificationsClient;
    readonly optionLists: IOptionListClient;
    readonly policies: IPoliciesClient;
    readonly querying: IQueryingClient;
    readonly raw: IRawClient;
    readonly settings: ISettingsClient;
    readonly users: IUsersClient;
    readonly pages: IPagesClient;
    readonly permissions: IPermissionsClient;
    readonly uploads: IUploadClient;
    readonly versionChecker: IVersionChecker;
    //#endregion

    //#region UI Clients
    readonly search: ISearchClient;
    //#endregion

    //#region Factories
    readonly entityFactory: IEntityFactory;
    readonly optionListFactory: IOptionListFactory;
    readonly relationFactory: IRelationFactory;
    //#endregion

    //#region Mappers
    readonly entityMapper: EntityMapper;
    readonly entityDefinitionMapper: EntityDefinitionMapper;
    //#endregion

    readonly linkHelper: ILinkHelper;

    impersonatedUsername: Nullable<string> = null;

    constructor(
        baseUri: URI | string,
        oauthGrant?: OAuthPasswordGrant | OAuthRefreshTokenGrant | OAuthClientCredentialsGrant,
        logger: ILogger = new NullLogger()
    ) {
        Guard.notNullOrUndefined(baseUri);
        Guard.notNull(oauthGrant);

        const baseUrl = typeof baseUri === "string" ? baseUri : baseUri.href();
        Guard.stringNotNullOrEmpty(baseUrl);

        this.logger = logger;

        this.internalClient = new InternalClient(baseUri, oauthGrant);

        this.api = new ApiClient(this);
        this.aggregates = new AggregateClient(this);
        this.assets = new AssetsClient(this);
        this.commands = new CommandsClient(this);
        this.entities = new EntitiesClient(this);
        this.entityDefinitions = new EntityDefinitionsClient(this);
        this.notifications = new NotificationsClient(this);
        this.notifications = new NotificationsClient(this);
        this.optionLists = new OptionListClient(this);
        this.pages = new PagesClient(this);
        this.policies = new PoliciesClient(this);
        this.querying = new QueryingClient(this);
        this.raw = new RawClient(this);
        this.search = new SearchClient(this);
        this.settings = new SettingsClient(this);
        this.users = new UsersClient(this);
        this.permissions = new PermissionsClient(this);
        this.uploads = new UploadClient(this);

        const cultureLoader = new CultureLoader(this);
        this.cultures = new CultureClient(cultureLoader);

        this.relationFactory = new RelationFactory(this);
        const typedEntityCreator = new TypedEntityCreator(this);
        this.entityFactory = new EntityFactory(this, this.relationFactory, cultureLoader, typedEntityCreator);
        this.optionListFactory = new OptionListFactory();

        this.entityMapper = new EntityMapper(this);
        this.entityDefinitionMapper = new EntityDefinitionMapper(this);

        this.linkHelper = new LinkHelper(this);

        this.versionChecker = new VersionChecker(this.api);
    }
}
